import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/auth-store';
import { useState } from 'react';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    const { refreshToken, accessToken } = useAuthStore.getState();
    if (refreshToken && accessToken) {
      // Fire and forget - don't block logout
      fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ refreshToken }),
      }).catch(() => {});
    }
    queryClient.clear();
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: DashboardIcon },
    { path: '/registrations', label: 'Registrations', icon: RegistrationsIcon },
    { path: '/members', label: 'Members', icon: MembersIcon },
    { path: '/onboarding-review', label: 'Onboarding', icon: OnboardingIcon },
    { path: '/banking-review', label: 'Banking', icon: BankingIcon },
    { path: '/funding-applications', label: 'Funding', icon: FundingIcon },
    { path: '/program-settings', label: 'Programs', icon: ProgramsIcon },
    { path: '/corrections', label: 'Help', icon: HelpIcon },
    { path: '/forms', label: 'Forms', icon: FormsIcon },
    { path: '/content', label: 'Content', icon: ContentIcon },
    { path: '/meetings', label: 'Meetings', icon: MeetingsIcon },
  ];

  const getPageTitle = () => {
    if (location.pathname === '/dashboard') return 'Dashboard';
    if (location.pathname === '/registrations') return 'Registrations';
    if (location.pathname === '/members') return 'Members';
    if (location.pathname === '/onboarding-review') return 'Onboarding Review';
    if (location.pathname === '/banking-review') return 'Banking Review';
    if (location.pathname === '/funding-applications') return 'Funding Applications';
    if (location.pathname === '/program-settings') return 'Program Settings';
    if (location.pathname === '/corrections') return 'Help & Corrections';
    if (location.pathname === '/forms') return 'Forms';
    if (location.pathname === '/content') return 'Content';
    if (location.pathname.startsWith('/meetings')) return 'Meetings';
    return 'Dashboard';
  };

  return (
    <div className="min-h-screen bg-[#e8d8c9]">
      {/* Skip to content link */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[200] focus:bg-white focus:px-4 focus:py-2 focus:rounded-lg"
      >
        Skip to content
      </a>

      {/* Shell Container */}
      <div className="shell">
        {/* Sidebar */}
        <aside className={`sidebar ${mobileMenuOpen ? 'open' : ''}`}>
          {/* Close button - Mobile only */}
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="sideclose md:hidden"
            aria-label="Close menu"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.65}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Brand */}
          <div className="brand">
            <Link to="/dashboard">
              <img src="/assets/ocn-w-logo.png" alt="OCN" width={114} height={40} />
            </Link>
            <small>Administration</small>
          </div>

          {/* Portal Label */}
          <div className="portallabel">
            <svg
              className="icon"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.65}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <strong>Admin Portal</strong>
            <svg
              className="icon ml-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.65}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {/* Navigation */}
          <nav className="nav">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={isActive ? 'current' : ''}
                >
                  <Icon />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Bottom Section */}
          <div className="sidebottom">
            <button className="side-support">
              <svg
                className="icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.65}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              Help & support
              <svg
                className="icon ml-auto"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.65}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <div className="account">
              <div className="avatar">
                {user?.firstName?.[0]}
                {user?.lastName?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <strong>
                  {user?.firstName} {user?.lastName}
                </strong>
                <small>Administrator</small>
              </div>
              <button onClick={handleLogout} className="iconbtn ghost" aria-label="Logout">
                <svg
                  className="icon"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.65}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
              </button>
            </div>
          </div>
        </aside>

        {/* Drawer Scrim - Mobile */}
        {mobileMenuOpen && (
          <div className="drawer-scrim" onClick={() => setMobileMenuOpen(false)} />
        )}

        {/* Workspace */}
        <div className="workspace">
          {/* Topbar */}
          <header className="topbar between">
            <div className="breadcrumb">
              <button
                aria-label="Open menu"
                onClick={() => setMobileMenuOpen(true)}
                className="iconbtn ghost mobilemenu"
              >
                <svg
                  className="icon"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.65}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <svg
                className="icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.65}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span>Admin Portal</span>
              <i>/</i>
              <strong>{getPageTitle()}</strong>
            </div>

            <div className="topactions">
              <div className="searchbar">
                <svg
                  className="icon"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.65}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input type="text" placeholder="Search..." />
                <span className="key">⌘K</span>
              </div>

              <button className="notification-button" aria-label="Notifications">
                <svg
                  className="icon"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.65}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
                <span></span>
              </button>

              <div className="header-avatar">
                {user?.firstName?.[0]}
                {user?.lastName?.[0]}
              </div>
            </div>
          </header>

          {/* Main Content */}
          <main className="main" id="main">
            {children}
          </main>
        </div>
      </div>

      <style>{`
        .shell {
          display: grid;
          grid-template-columns: 221px minmax(0, 1fr);
          min-height: calc(100svh - 32px);
          max-width: 1850px;
          margin: 16px auto;
          border-radius: 25px;
          background: #f6f6f3;
          border: 1px solid #d7d5cf;
          overflow: clip;
          width: calc(100% - 32px);
          position: relative;
        }

        .sidebar {
          position: sticky;
          top: 28px;
          height: calc(100svh - 56px);
          min-height: 640px;
          background: #fbfaf7;
          border: 1px solid #e5e4df;
          color: #383c3b;
          border-radius: 13px;
          margin: 12px 0 12px 12px;
          padding: 27px 13px 16px;
          display: flex;
          flex-direction: column;
          z-index: 30;
          align-self: start;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: #565956 transparent;
        }

        .sideclose {
          position: absolute;
          top: 23px;
          right: 13px;
          background: transparent;
          color: #70776b;
          width: 31px;
          height: 31px;
          min-height: 31px;
          border: 0;
          padding: 5px;
          display: grid;
          place-items: center;
        }

        .brand {
          display: block;
          padding: 0 12px 26px;
        }

        .brand img {
          width: 114px;
          height: auto;
        }

        .brand small {
          display: block;
          font-size: 10px;
          color: #7b7d77;
          letter-spacing: 0.1px;
          margin: 7px 0 0 3px;
        }

        .portallabel {
          border: 1px solid #e5e2dc;
          background: #f2eee8;
          border-radius: 9px;
          margin: 0 0 26px;
          padding: 11px 12px;
          gap: 9px;
          color: #6f665e;
          font-size: 12px;
          display: flex;
          align-items: center;
        }

        .portallabel strong {
          font-weight: 500;
        }

        .portallabel > .icon:first-child {
          width: 16px;
          height: 16px;
        }

        .portallabel > .icon:last-child {
          width: 13px;
          height: 13px;
        }

        .nav {
          display: flex;
          flex-direction: column;
          gap: 7px;
        }

        .nav a {
          font-size: 13px;
          padding: 12px;
          gap: 11px;
          border-radius: 9px;
          color: #747975;
          min-height: 43px;
          transition: background 0.2s, color 0.2s;
          display: flex;
          align-items: center;
        }

        .nav a .icon {
          width: 17px;
          height: 17px;
          color: #92968c;
        }

        .nav a:hover {
          background: #f1efea;
          color: #323833;
        }

        .nav a.current {
          background: #e8d8c9;
          color: #3c3934;
          font-weight: 600;
        }

        .nav a.current .icon {
          color: #5c554d;
        }

        .sidebottom {
          margin-top: auto;
          padding-top: 36px;
        }

        .side-support {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          background: #f1eee7;
          border: 1px solid #e9e4da;
          color: #827769;
          border-radius: 9px;
          font-size: 12px;
          text-align: left;
          margin-bottom: 12px;
        }

        .side-support .icon {
          width: 16px;
          height: 16px;
        }

        .side-support:hover {
          background: #e9e3d9;
        }

        .account {
          border-top: 1px solid #e5e2da;
          margin: 15px 2px 0;
          padding-top: 17px;
          gap: 9px;
          display: flex;
          align-items: center;
        }

        .account .avatar {
          height: 31px;
          width: 31px;
          font-size: 10px;
          background: #e1e5eb;
          color: #4b607f;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          font-weight: 500;
        }

        .account strong {
          font-size: 11px;
          font-weight: 500;
          color: #585f53;
          display: block;
        }

        .account small {
          font-size: 10px;
          color: #8e9685;
          display: block;
        }

        .iconbtn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 10px;
          width: 44px;
          height: 44px;
          min-height: 44px;
          border: 1px solid #e6e9de;
          background: white;
          border-radius: 8px;
          transition: background 0.18s;
        }

        .iconbtn.ghost {
          background: transparent;
          border-color: transparent;
        }

        .iconbtn .icon {
          width: 18px;
          height: 18px;
        }

        .workspace {
          margin: 0;
          width: auto;
          min-width: 0;
        }

        .topbar {
          height: 91px;
          padding: 0 30px;
          background: transparent;
          border-bottom: 0;
          gap: 15px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .breadcrumb {
          font-size: 12px;
          gap: 12px;
          display: flex;
          align-items: center;
        }

        .breadcrumb > span {
          color: #68716d;
        }

        .breadcrumb > i {
          font-style: normal;
          color: #68716d;
          font-size: 13px;
        }

        .breadcrumb strong {
          color: #424a47;
          font-weight: 500;
        }

        .breadcrumb .icon {
          width: 18px;
          height: 18px;
          color: #68716d;
        }

        .mobilemenu {
          display: none;
        }

        .topactions {
          gap: 13px;
          display: flex;
          align-items: center;
        }

        .searchbar {
          padding: 9px 13px;
          background: white;
          border-radius: 9px;
          gap: 9px;
          color: #68716d;
          min-height: 38px;
          display: flex;
          align-items: center;
        }

        .searchbar input {
          font-size: 12px;
          width: 165px;
          padding: 0;
          min-height: 20px;
          border: 0;
          background: none;
          outline: 0;
        }

        .searchbar .icon {
          width: 14px;
          height: 14px;
        }

        .searchbar .key {
          border: 0;
          background: #f2f3ed;
          font-size: 10px;
          color: #68716d;
          line-height: 17px;
          padding: 1px 5px;
          border-radius: 4px;
        }

        .notification-button {
          height: 37px;
          min-height: 37px;
          width: 37px;
          border-radius: 10px;
          background: #fff;
          border: 0;
          position: relative;
          color: #68716d;
          display: grid;
          place-items: center;
        }

        .notification-button > span {
          position: absolute;
          top: 8px;
          right: 9px;
          border: 1px solid #fff;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: #f3701e;
        }

        .notification-button .icon {
          width: 18px;
          height: 18px;
        }

        .header-avatar {
          width: 35px;
          height: 35px;
          border-radius: 50%;
          background: #eee0d2;
          color: #5f5548;
          font-size: 11px;
          outline: 3px solid white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 500;
        }

        .main {
          padding: 3px 30px 24px;
          max-width: none;
          margin: 0;
          min-width: 0;
        }

        .icon {
          width: 18px;
          height: 18px;
          fill: none;
          stroke: currentColor;
          stroke-width: 1.65;
          stroke-linecap: round;
          stroke-linejoin: round;
          flex-shrink: 0;
        }

        .drawer-scrim {
          display: none;
        }

        @media (max-width: 1023px) {
          .shell {
            display: block;
            width: 100%;
            margin: 0;
            border-radius: 0;
            border: 0;
            min-height: 100svh;
          }

          .sidebar {
            position: fixed;
            top: 10px;
            left: 10px;
            bottom: 10px;
            height: calc(100svh - 20px);
            width: 230px;
            min-height: 0;
            margin: 0;
            padding: 27px 16px 17px;
            border-radius: 16px;
            transform: translateX(-260px);
            visibility: hidden;
            transition: transform 0.2s, visibility 0.2s;
          }

          .sidebar.open {
            transform: translateX(0);
            visibility: visible;
          }

          .sidebar.open + .drawer-scrim {
            display: block;
            position: fixed;
            inset: 0;
            background: #24272855;
            z-index: 25;
            backdrop-filter: blur(2px);
          }

          .workspace {
            margin: 0;
            width: 100%;
          }

          .topbar {
            height: 75px;
            padding: 0 18px;
          }

          .searchbar {
            display: none;
          }

          .mobilemenu {
            display: inline-flex;
            width: 30px;
            min-height: 30px;
            height: 30px;
            padding: 5px;
            border: 0;
            background: transparent;
            color: #68716d;
          }

          .breadcrumb > span,
          .breadcrumb > i,
          .breadcrumb > .icon {
            display: none;
          }

          .notification-button {
            height: 32px;
            min-height: 32px;
            width: 32px;
            padding: 7px;
          }

          .notification-button .icon {
            height: 17px;
            width: 17px;
          }

          .topactions .header-avatar {
            display: grid;
            height: 32px;
            width: 32px;
            font-size: 10px;
          }

          .main {
            padding: 5px 18px 22px;
          }
        }

        @media (min-width: 1550px) {
          .shell {
            grid-template-columns: 242px minmax(0, 1fr);
          }

          .sidebar {
            padding: 29px 19px 18px;
          }

          .topbar {
            padding: 0 37px;
            height: 98px;
          }

          .main {
            padding: 4px 37px 28px;
          }
        }
      `}</style>
    </div>
  );
}

// Icon Components
function DashboardIcon() {
  return (
    <svg className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.65}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
      />
    </svg>
  );
}

function RegistrationsIcon() {
  return (
    <svg className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.65}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
      />
    </svg>
  );
}

function MembersIcon() {
  return (
    <svg className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.65}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

function FormsIcon() {
  return (
    <svg className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.65}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
      />
    </svg>
  );
}

function ContentIcon() {
  return (
    <svg className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.65}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function MeetingsIcon() {
  return (
    <svg className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.65}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  );
}

function OnboardingIcon() {
  return (
    <svg className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.65}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function BankingIcon() {
  return (
    <svg className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.65}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
      />
    </svg>
  );
}

function FundingIcon() {
  return (
    <svg className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.65}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function ProgramsIcon() {
  return (
    <svg className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.65}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function HelpIcon() {
  return (
    <svg className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.65}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}
